import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BackButton } from "@/components/ui/back-button";
import { Card } from "@/components/ui/card";
import { AlertTriangle, Trash2, Settings, ChevronRight, Clock, Database, Shield } from "lucide-react";

const DeleteAccount = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto pt-16">
        <div className="fixed top-4 left-4 z-50">
          <BackButton onClick={() => navigate(-1)} />
        </div>

        <h1 className="text-4xl font-bold mb-4">{t('delete_account_page.title')}</h1>
        <p className="text-lg text-muted-foreground mb-8">
          {t('delete_account_page.subtitle')}
        </p>

        <div className="space-y-8 text-foreground/90">
          {/* How to Delete Section */}
          <Card className="p-6 border-2 border-primary bg-primary/5">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
              <Trash2 className="w-6 h-6 text-primary" />
              {t('delete_account_page.how_to_title')}
            </h2>
            <p className="mb-6 text-muted-foreground">
              {t('delete_account_page.how_to_desc')}
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-background rounded-lg border">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                  1
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{t('delete_account_page.step_1_title')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('delete_account_page.step_1_desc')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-background rounded-lg border">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                  2
                </div>
                <div>
                  <h3 className="font-semibold mb-1 flex items-center gap-2">
                    {t('delete_account_page.step_2_title')}
                    <Settings className="w-4 h-4 text-muted-foreground" />
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('delete_account_page.step_2_desc')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-background rounded-lg border">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                  3
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{t('delete_account_page.step_3_title')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('delete_account_page.step_3_desc')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-background rounded-lg border">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center font-bold">
                  4
                </div>
                <div>
                  <h3 className="font-semibold mb-1 text-destructive flex items-center gap-2">
                    {t('delete_account_page.step_4_title')}
                    <Trash2 className="w-4 h-4" />
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('delete_account_page.step_4_desc')}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm">
                {t('delete_account_page.important_note')}
              </p>
            </div>
          </Card>

          {/* Data Deleted Section */}
          <section>
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
              <Database className="w-6 h-6 text-destructive" />
              {t('delete_account_page.data_deleted_title')}
            </h2>
            <p className="mb-4 text-muted-foreground">
              {t('delete_account_page.data_deleted_desc')}
            </p>
            <Card className="p-6 border-destructive/30 bg-destructive/5">
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-destructive">✗</span>
                  <span>{t('delete_account_page.deleted_account')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-destructive">✗</span>
                  <span>{t('delete_account_page.deleted_content')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-destructive">✗</span>
                  <span>{t('delete_account_page.deleted_tracking')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-destructive">✗</span>
                  <span>{t('delete_account_page.deleted_goals')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-destructive">✗</span>
                  <span>{t('delete_account_page.deleted_social')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-destructive">✗</span>
                  <span>{t('delete_account_page.deleted_subs')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-destructive">✗</span>
                  <span>{t('delete_account_page.deleted_notifications')}</span>
                </li>
              </ul>
            </Card>
          </section>

          {/* Data Retention Section */}
          <section>
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-6 h-6 text-primary" />
              {t('delete_account_page.retention_title')}
            </h2>
            <Card className="p-6 bg-card/50 border-border/50">
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">{t('delete_account_page.retention_immediate_title')}</h3>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                    <li>{t('delete_account_page.retention_immediate_1')}</li>
                    <li>{t('delete_account_page.retention_immediate_2')}</li>
                    <li>{t('delete_account_page.retention_immediate_3')}</li>
                    <li>{t('delete_account_page.retention_immediate_4')}</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">{t('delete_account_page.retention_30_title')}</h3>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                    <li>{t('delete_account_page.retention_30_1')}</li>
                    <li>{t('delete_account_page.retention_30_2')}</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">{t('delete_account_page.retention_90_title')}</h3>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                    <li>{t('delete_account_page.retention_90_1')}</li>
                    <li>{t('delete_account_page.retention_90_2')}</li>
                  </ul>
                </div>
              </div>
            </Card>
          </section>

          {/* Data Retained Section */}
          <section>
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-amber-500" />
              {t('delete_account_page.retained_title')}
            </h2>
            <p className="mb-4 text-muted-foreground">
              {t('delete_account_page.retained_desc')}
            </p>
            <Card className="p-6 bg-amber-500/5 border-amber-500/30">
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-amber-500">•</span>
                  <span>{t('delete_account_page.retained_payment')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-amber-500">•</span>
                  <span>{t('delete_account_page.retained_security')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-amber-500">•</span>
                  <span>{t('delete_account_page.retained_analytics')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-amber-500">•</span>
                  <span>{t('delete_account_page.retained_legal')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-amber-500">•</span>
                  <span>{t('delete_account_page.retained_abuse')}</span>
                </li>
              </ul>
            </Card>
          </section>

          {/* Third-Party Data Section */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">{t('delete_account_page.third_party_title')}</h2>
            <p className="mb-4 text-muted-foreground">
              {t('delete_account_page.third_party_desc')}
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
              <li>{t('delete_account_page.third_party_apple')}</li>
              <li>{t('delete_account_page.third_party_stripe')}</li>
              <li>{t('delete_account_page.third_party_revenuecat')}</li>
              <li>{t('delete_account_page.third_party_openai')}</li>
            </ul>
            <p className="mt-4 text-sm text-muted-foreground">
              {t('delete_account_page.third_party_note')}
            </p>
          </section>

          {/* Contact Section */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">{t('delete_account_page.help_title')}</h2>
            <p className="mb-4">
              {t('delete_account_page.help_desc_1')}{' '}
              <a href="/contact-us" className="text-primary hover:underline">{t('delete_account_page.help_link')}</a>.
            </p>
            <p className="text-muted-foreground">
              {t('delete_account_page.help_desc_2')}
            </p>
          </section>

          <section className="pt-8 border-t border-border">
            <p className="text-sm text-muted-foreground">
              {t('delete_account_page.last_updated')} {new Date().toLocaleDateString()}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {t('delete_account_page.privacy_info')}{' '}
              <a href="/privacy-policy" className="text-primary hover:underline">{t('nav.privacy_policy')}</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
